import React, { useEffect, useMemo, useState } from "react";
import { ref, set, push, get, update } from "firebase/database";
import { database } from "../../../firebaseConfig";
import toast from "react-hot-toast";
import { X, ShoppingCart } from "lucide-react";
import UnitPricePromptModal from "./UnitPricePromptModal";
import { logRecordAuditEvent } from "../../../utils/recordAuditLog";

const DEFAULT_CLIENT_UNIT_PRICE = 65;

const RecordPurchaseModal = ({ client, companyName, onClose, existingSale = null, onSaved }) => {
  const today = useMemo(() => new Date().toISOString().split("T")[0], []);

  // Determine if the client has a saved unit price (non-null, > 0)
  const clientSavedPrice = client?.unitPrice ?? client?.UnitPrice ?? null;
  const clientHasPrice = clientSavedPrice != null && parseFloat(clientSavedPrice) > 0;

  const [showPricePrompt, setShowPricePrompt] = useState(!clientHasPrice && !existingSale);
  const [quantity, setQuantity] = useState("");
  const [unitPrice, setUnitPrice] = useState(
    clientHasPrice ? String(clientSavedPrice) : String(DEFAULT_CLIENT_UNIT_PRICE)
  );
  const [paymentMode, setPaymentMode] = useState(client?.mode || "Cash");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(today);

  useEffect(() => {
    const savedPrice = client?.unitPrice ?? client?.UnitPrice ?? null;
    const hasPrice = savedPrice != null && parseFloat(savedPrice) > 0;

    if (existingSale) {
      const qtyValue = existingSale.quantity ?? existingSale.liters ?? "";
      setQuantity(qtyValue === "" ? "" : String(qtyValue));
      const unitValue =
        existingSale.unitPrice ??
        existingSale.UnitPrice ??
        savedPrice ??
        DEFAULT_CLIENT_UNIT_PRICE;
      setUnitPrice(unitValue === "" ? "" : String(unitValue));
      setPaymentMode(
        existingSale.paymentMode ||
          existingSale.PaymentMode ||
          existingSale.mode ||
          existingSale.Mode ||
          client?.mode ||
          "Cash"
      );
      setNotes(existingSale.notes || "");
      const existingDate = existingSale.purchaseDate
        ? new Date(existingSale.purchaseDate).toISOString().split("T")[0]
        : today;
      setSelectedDate(existingDate);
      setShowPricePrompt(false);
    } else {
      setQuantity("");
      setUnitPrice(hasPrice ? String(savedPrice) : String(DEFAULT_CLIENT_UNIT_PRICE));
      setPaymentMode(client?.mode || "Cash");
      setNotes("");
      setSelectedDate(today);
      setShowPricePrompt(!hasPrice);
    }
  }, [existingSale, client, today]);

  if (!client) return null;

  // Show price prompt before the main form when no saved price exists
  if (showPricePrompt) {
    return (
      <UnitPricePromptModal
        name={client.name}
        type="client"
        defaultPrice={DEFAULT_CLIENT_UNIT_PRICE}
        onConfirm={(confirmedPrice) => {
          setUnitPrice(String(confirmedPrice));
          setShowPricePrompt(false);
        }}
        onCancel={onClose}
      />
    );
  }

  const totalAmount = (parseFloat(quantity) || 0) * (parseFloat(unitPrice) || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!quantity || parseFloat(quantity) <= 0) {
      toast.error("Please enter a valid quantity");
      return;
    }

    if (!unitPrice || parseFloat(unitPrice) <= 0) {
      toast.error("Please enter a valid unit price");
      return;
    }

    setLoading(true);
    try {
      const now = existingSale?.purchaseDate
        ? new Date(existingSale.purchaseDate)
        : new Date(selectedDate + "T00:00:00");
      const purchaseId = existingSale?.purchaseId || existingSale?.id || push(ref(database, "temp")).key;
      const purchaseDate = existingSale?.purchaseDate || now.toISOString();
      const recordedAt =
        existingSale?.recordedAt || existingSale?.recordedAtTimestamp || new Date().toISOString();
      const year = now.getFullYear();
      const month = now.toLocaleString("en-US", { month: "long" });
      const day = now.getDate().toString();

      // Keep both quantity and liters for compatibility with existing readers that expect either key.
      const purchaseData = {
        purchaseId,
        clientId: client.id,
        clientName: client.name,
        quantity: parseFloat(quantity),
        liters: parseFloat(quantity),
        unitPrice: parseFloat(unitPrice),
        totalAmount,
        paymentMode,
        notes,
        purchaseDate,
        recordedAt,
        status: existingSale?.status || "Completed",
        recordedBy: existingSale?.recordedBy || companyName,
      };

      // Save purchase record
      const purchasePath = `Companies/${companyName}/ClientPurchases/${client.id}/purchases/${year}/${month}/${purchaseId}`;
      const dailyCompanyPath = `Companies/${companyName}/MilkSold/${year}/${month}/${day}/${purchaseId}`;
      const newSaleCompanyPath = `Companies/${companyName}/NewMilkSaleRecord/${purchaseId}`;
      const newSaleRootPath = `NewMilkSaleRecord/${purchaseId}`;

      const updates = {
        [purchasePath]: purchaseData,
        [dailyCompanyPath]: purchaseData,
        [newSaleCompanyPath]: purchaseData,
        [newSaleRootPath]: purchaseData,
      };
      await update(ref(database), updates);

      if (existingSale) {
        await logRecordAuditEvent({
          action: "edit",
          entityType: "milk_sale_record",
          companyName,
          recordId: purchaseId,
          sourceComponent: "MilkSoldTab",
          oldData: existingSale,
          newData: purchaseData,
        });
      }

      if (!existingSale) {
        // If on credit, update client balance and shops_receivable
        if (paymentMode === "Credit") {
          const currentBalance = parseFloat(client.balance) || 0;
          const newBalance = currentBalance - totalAmount;

          // Update balance in both locations with error handling
          try {
            await set(
              ref(database, `Companies/${companyName}/ValidatedClients/${client.id}/Balance`),
              newBalance
            );

            await set(
              ref(database, `Companies/${companyName}/AIValidatedClients/${client.id}/Balance`),
              newBalance
            );

            // Update shops_receivable account - increase by purchase amount
            const shopsReceivableRef = ref(database, `Companies/${companyName}/AccountBalances/shop_receivable`);
            const shopsReceivableSnap = await get(shopsReceivableRef);
            const currentShopsReceivable = shopsReceivableSnap.exists()
              ? (parseFloat(shopsReceivableSnap.val().balance) || 0)
              : 0;
            const newShopsReceivable = currentShopsReceivable + totalAmount;

            await set(shopsReceivableRef, {
              balance: newShopsReceivable,
              lastUpdated: new Date().toISOString(),
            });

            // Record transaction in shops_receivable account
            const transactionId = push(ref(database, "temp")).key;
            const transactionPath = `Companies/${companyName}/Accounts/shop_receivable/${year}/${month}/${transactionId}`;
            await set(ref(database, transactionPath), {
              id: transactionId,
              date: new Date().toISOString(),
              amount: totalAmount,
              direction: "debit",
              type: "credit_purchase",
              clientId: client.id,
              clientName: client.name,
              notes: `Milk purchase on credit - ${quantity}L @ KES ${unitPrice}/L`,
              reference: purchaseId,
            });
          } catch (balanceError) {
            console.error("Error updating client balance:", balanceError);
            // Still show success for purchase record, but warn about balance
            toast.warning("Purchase recorded but balance update may have failed. Please verify.");
          }
        } else {
          // If paid immediately (Cash/M-Pesa/Bank Transfer), update company_cash or company_bank
          try {
            // M-Pesa and Bank Transfer go to company_bank, only Cash goes to company_cash
            const accountType = (paymentMode === "Cash") ? "company_cash" : "company_bank";
            const accountRef = ref(database, `Companies/${companyName}/AccountBalances/${accountType}`);
            const accountSnap = await get(accountRef);
            const currentBalance = accountSnap.exists()
              ? (parseFloat(accountSnap.val().balance) || 0)
              : 0;
            const newBalance = currentBalance + totalAmount;

            await set(accountRef, {
              balance: newBalance,
              lastUpdated: new Date().toISOString(),
            });

            // Record transaction
            const transactionId = push(ref(database, "temp")).key;
            const transactionPath = `Companies/${companyName}/Accounts/${accountType}/${year}/${month}/${transactionId}`;
            await set(ref(database, transactionPath), {
              id: transactionId,
              date: new Date().toISOString(),
              amount: totalAmount,
              direction: "credit",
              type: "milk_sale",
              clientId: client.id,
              clientName: client.name,
              paymentMode: paymentMode,
              notes: `Milk sale - ${quantity}L @ KES ${unitPrice}/L`,
              reference: purchaseId,
            });
          } catch (error) {
            console.error("Error updating company account:", error);
          }
        }
      }

      toast.success(existingSale ? "Purchase updated successfully" : `Purchase of ${quantity} liters recorded successfully`);
      onSaved?.();
      onClose();
    } catch (error) {
      console.error("Error recording purchase:", error);
      toast.error("Failed to record purchase. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-cyan-700 flex items-center gap-2">
            <ShoppingCart size={24} />
            Record Milk Purchase
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X size={24} />
          </button>
        </div>

        <div className="mb-4 p-3 bg-cyan-50 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>Client:</strong> {client.name}
          </p>
          <p className="text-sm text-gray-700">
            <strong>Current Balance:</strong> KES {parseFloat(client.balance || 0).toLocaleString()}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Purchase Date *
              </label>
              <input
                type="date"
                value={selectedDate}
                max={today}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantity (Liters) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="Enter quantity in liters"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit Price per Liter (KES) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="Price per liter"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Mode *
              </label>
              <select
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                required
              >
                <option value="Cash">Cash</option>
                <option value="M-Pesa">M-Pesa</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Credit">Credit (Add to Balance)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                rows="2"
                placeholder="Any additional notes..."
              />
            </div>

            {quantity && unitPrice && (
              <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                <p className="text-sm text-cyan-800">
                  <strong>Total Amount:</strong> KES {totalAmount.toLocaleString()}
                </p>
                {paymentMode === "Credit" && (
                  <p className="text-sm text-cyan-800 mt-1">
                    <strong>New Balance:</strong> KES{" "}
                    {(parseFloat(client.balance || 0) - totalAmount).toLocaleString()}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition disabled:bg-cyan-300"
              disabled={loading}
            >
              {loading ? "Recording..." : "Record Purchase"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecordPurchaseModal;
